import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@/components/hooks/use-toast';

import {
  selectSessionAdvisor,
  selectClientDuplicationStatus,
  postAdvisorNewDuplicateClientRequest,
  resetClientDuplicationStatus,
} from '@client/model';
import { ActionStatus } from '@shared';

interface ClientDuplicationProps {
  open: boolean;
  client: Client;
  onClose: () => void;
}

export function useClientDuplication({ open, onClose, client }: ClientDuplicationProps) {
  const { toast } = useToast();
  const dispatch = useDispatch();

  const advisor = useSelector(selectSessionAdvisor);
  const clientDupStatus = useSelector(selectClientDuplicationStatus);
  const [copyRelatedData, setCopyRelatedData] = useState(true);

  const isLoading = clientDupStatus === ActionStatus.loading;
  const isDuplicated = clientDupStatus === ActionStatus.duplicated;
  const isError = clientDupStatus === ActionStatus.error;

  useEffect(() => {
    if (open && isDuplicated) {
      toast({
        title: 'Success!',
        variant: 'default',
        description: 'Client has been duplicated successfully.',
      });

      dispatch(resetClientDuplicationStatus());
      onClose();
    }
  }, [isDuplicated, open, toast, onClose, dispatch]);

  useEffect(() => {
    if (open && isError) {
      toast({
        title: 'Error',
        variant: 'destructive',
        description: 'Failed to duplicate client. Please try again.',
      });

      dispatch(resetClientDuplicationStatus());
    }
  }, [isError, open, toast, dispatch]);

  function handleClientDuplication() {
    if (!isLoading) {
      dispatch(
        postAdvisorNewDuplicateClientRequest({
          originalClientId: client.clientId,
          advisorId: advisor.advisorId,
          params: {
            lastName: client.clientLastName,
            firstName: client.clientFirstName,
            clientEmail: client.clientEmail,
            copyRelatedData,
          },
        }),
      );
    }
  }

  function handleOnClose() {
    // This function was created for future requirements
    onClose();
  }

  return {
    isDuplicated,
    isLoading,
    isError,
    copyRelatedData,
    setCopyRelatedData,
    handleClientDuplication,
    handleOnClose
  };
}
